import React from 'react';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * AccessDenied — inline component for 403 responses on data-loading operations.
 *
 * variant "page"    — full-height centered, used when the whole page/view is blocked.
 * variant "section" — compact, used inside a table, card, or panel section.
 *
 * Props:
 *   feature  — human-readable name of what was blocked ("Lab Tracker", "Patient Management")
 *   message  — override the auto-generated message entirely
 *   variant  — "page" | "section" (default: "page")
 *   className
 */
export function AccessDenied({ feature, message, variant = 'page', className }) {
  const body = message || (
    feature
      ? `You don't have access to ${feature}. Contact your administrator to request access.`
      : "You don't have permission to view this content. Contact your administrator to request access."
  );

  if (variant === 'section') {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2 py-10 text-center', className)}>
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-50 border border-red-100">
          <Lock className="w-5 h-5 text-red-400" />
        </div>
        <p className="text-sm font-medium text-gray-700">Access Denied</p>
        <p className="text-xs text-gray-400 max-w-xs">{body}</p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-4', className)}>
      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50 border border-red-100">
        <Lock className="w-8 h-8 text-red-400" />
      </div>
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-gray-800">Access Denied</h2>
        <p className="text-sm text-gray-500 max-w-sm">{body}</p>
      </div>
    </div>
  );
}
